/**
 * Single place that turns a failed API response into the message the add-in shows.
 *
 * The add-in must display the error BuilderMetrix actually reported, so this understands every
 * shape the response can arrive in rather than guessing at a cause:
 *
 *  - `{ ErrorMessage, ErrorCode }` — the backend's ExceptionHandlingMiddleware, which serialises
 *    ErrorDTO with the default (Pascal-case) options.
 *  - `{ errorMessage, errorCode }` — the same DTO returned from a controller, where MVC's
 *    camel-case policy applies.
 *  - `{ statusCode, message, exceptionMessage }` — BMX's own error envelope.
 *  - `{ title, detail, errors }` — RFC 7807 ProblemDetails from model binding; a `required`
 *    property missing from the request body lands here.
 *  - A bare JSON string, or a plain-text body from a controller returning `BadRequest("...")`.
 *
 * Anything genuinely unusable (an HTML error page, an empty body, or .NET's
 * "Exception of type ... was thrown." placeholder) falls back to a plain description of the
 * status code. That is honest about what happened without inventing a cause.
 */

import { ErrorMessages, StatusMessages, unexpectedStatusMessage } from "../constants/messages";

export type ApiError = {
  status: number;
  code?: string;
  message: string;
};

// Long enough for a real validation message, short enough that a stray HTML page or serialised
// stack trace can never become the banner text.
const MAX_MESSAGE_LENGTH = 1000;

// Text that carries no information about the failure and must never reach the user:
//  - the message .NET generates for an exception built without one;
//  - the literal "None", which BuilderMetrix's exception middleware seeds its message with and
//    leaves in place for a not-found error.
const PLACEHOLDER_PATTERN = /^none$|exception of type .* was thrown/i;

function isUsable(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "" && !PLACEHOLDER_PATTERN.test(value.trim());
}

function clamp(message: string): string {
  const collapsed = message.replace(/\s+/g, " ").trim();
  return collapsed.length <= MAX_MESSAGE_LENGTH ? collapsed : `${collapsed.slice(0, MAX_MESSAGE_LENGTH).trimEnd()}…`;
}

/** A plain description of a status code, used only when the body carries no message. */
export function describeStatus(status: number): string {
  const known = StatusMessages[status];
  if (known) {
    return known;
  }

  // A status of 0 means the request never reached BuilderMetrix (offline, blocked, DNS).
  return status > 0 ? unexpectedStatusMessage(status) : ErrorMessages.Offline;
}

/**
 * Flattens a ProblemDetails `errors` map, or a BMX validation-failure list, into one sentence.
 *
 * An entry that names its own message field is read through `readErrorObject` — a validation
 * failure serialises as `{ PropertyName, ErrorMessage }`, where only ErrorMessage is worth
 * showing. Everything else is walked by its values, because in a field -> messages map the keys
 * are field names, not message fields.
 */
function readErrorMap(value: unknown): string | null {
  if (typeof value !== "object" || value === null) {
    return isUsable(value) ? value.trim() : null;
  }

  if (!Array.isArray(value)) {
    const named = readErrorObject(value as Record<string, unknown>);
    if (named) {
      return named;
    }
  }

  const entries = Array.isArray(value) ? value : Object.values(value as Record<string, unknown>);
  const messages: string[] = [];

  for (const entry of entries) {
    if (isUsable(entry)) {
      messages.push(entry.trim());
      continue;
    }

    const nested = readErrorMap(entry);
    if (nested) {
      messages.push(nested);
    }
  }

  return messages.length > 0 ? Array.from(new Set(messages)).join(" ") : null;
}

function readErrorObject(body: Record<string, unknown>): string | null {
  // Ordered most specific first. `title` is deliberately excluded here: on a validation
  // ProblemDetails it is the generic "One or more validation errors occurred.", which would mask
  // the per-field messages under `errors` that are checked next.
  for (const field of ["ErrorMessage", "errorMessage", "Message", "message", "detail", "Detail"]) {
    const value = body[field];
    if (isUsable(value)) {
      return value.trim();
    }
  }

  const mapped = readErrorMap(body.errors ?? body.Errors ?? body.failures ?? body.Failures);
  if (mapped) {
    return mapped;
  }

  // Generic summaries, used only once the specific fields above are exhausted.
  for (const field of ["title", "Title", "error", "Error"]) {
    const value = body[field];
    if (isUsable(value)) {
      return value.trim();
    }
  }

  const exceptionMessage = body.exceptionMessage ?? body.ExceptionMessage;
  return isUsable(exceptionMessage) ? exceptionMessage.trim() : null;
}

/** Extracts a message from a raw response body, or null when it carries nothing usable. */
export function readErrorBody(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed || trimmed.startsWith("<")) {
    // An empty body, or a gateway/host HTML error page: neither is a message for the user.
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(trimmed);

    if (isUsable(parsed)) {
      return clamp(parsed.trim());
    }

    if (Array.isArray(parsed)) {
      const joined = readErrorMap(parsed);
      return joined ? clamp(joined) : null;
    }

    if (typeof parsed === "object" && parsed !== null) {
      const message = readErrorObject(parsed as Record<string, unknown>);
      return message ? clamp(message) : null;
    }

    return null;
  } catch {
    // Not JSON: a controller returned a bare string, which ASP.NET Core writes as text/plain.
    return isUsable(trimmed) ? clamp(trimmed) : null;
  }
}

/**
 * Reads a failed response into its status, code, and the message to display.
 * Never throws — a body that cannot be read falls back to the status description.
 */
export async function readApiError(response: Response, fallback?: string): Promise<ApiError> {
  let text = "";
  try {
    text = await response.text();
  } catch {
    // Body already consumed or the stream failed; the status still tells us something.
  }

  const message = readErrorBody(text);
  let code: string | undefined;

  if (text) {
    try {
      const parsed: unknown = JSON.parse(text);
      if (typeof parsed === "object" && parsed !== null) {
        const raw = parsed as Record<string, unknown>;
        const value = raw.ErrorCode ?? raw.errorCode;
        if (typeof value === "string" && value.trim() !== "") {
          code = value.trim();
        }
      }
    } catch {
      // Not JSON, so there is no error code to read.
    }
  }

  return {
    status: response.status,
    code,
    message: message ?? fallback ?? describeStatus(response.status),
  };
}

/**
 * Builds an Error carrying the message the server reported, for `throw await responseError(...)`.
 * `fallback` is used only when the response body has no usable message.
 */
export async function responseError(response: Response, fallback?: string): Promise<Error> {
  const { message } = await readApiError(response, fallback);
  return new Error(message);
}

/**
 * Message for a caught value. Use instead of `String(err)`, which renders an Error as
 * "Error: ..." and a plain object as "[object Object]".
 */
export function getErrorMessage(err: unknown, fallback: string = ErrorMessages.Unexpected): string {
  if (err instanceof Error) {
    return isUsable(err.message) ? clamp(err.message) : fallback;
  }

  if (isUsable(err)) {
    return clamp(err.trim());
  }

  if (typeof err === "object" && err !== null) {
    const message = readErrorObject(err as Record<string, unknown>);
    if (message) {
      return clamp(message);
    }
  }

  return fallback;
}
